import AnimationController, {Curves} from '@/animation/animation-controller';
import {logError} from '@/utilities/log';
import classNames from 'classnames';
import {useCallback, useEffect, useRef, useState} from 'preact/hooks';
import Draggable, {DraggableEventHandler} from 'react-draggable';

export type BeforeAfterSliderProps = {
  /** Selector of the element on which to set '--clip-path-offset' CSS variable and data-initial-state attribute. (For animating the liquid-rendered elements using Tailwind) */
  rootSelector?: string;
  /** The % of total width to offset the slider from the left side of its container on initial render */
  initialSliderPosition?: number | string;
};

export default function BeforeAfterSlider({
  initialSliderPosition = 50,
  rootSelector = 'body',
}: BeforeAfterSliderProps) {
  const [shouldPulsate, setShouldPulsate] = useState(false);
  const [posX, setPosX] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDrag: DraggableEventHandler = (e, data) => {
    setOffsetX(data.x);
    setShouldPulsate(false);
  };

  const setOffsetX = useCallback(
    (offsetX: number): void => {
      setPosX(offsetX);
      const parent = document.querySelector(rootSelector);

      if (!parent) return;

      (parent as HTMLElement).style.setProperty(
        '--clip-path-offset',
        `${offsetX}px`
      );
    },
    [rootSelector]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const {width} = containerRef.current.getBoundingClientRect();
    const targetOffset = Math.round(
      (Number(initialSliderPosition) * width) / 100
    );

    const animationController = new AnimationController({
      durationMs: 1000,
      easingFunction: Curves.easeOutCubic,
    });

    animationController.addListener((value) =>
      setOffsetX(targetOffset * value)
    );

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;

        animationController
          .forward()
          .then(() => {
            setShouldPulsate(true);
          })
          .catch(logError);

        const parent = document.querySelector(rootSelector);

        (parent as HTMLElement | null)?.setAttribute(
          'data-initial-state',
          'false'
        );

        observer.unobserve(entry.target);
      },
      {
        threshold: 0.5,
      }
    );

    observer.observe(containerRef.current);

    return () => {
      animationController.clearListeners();
      observer.disconnect();
    };
  }, [containerRef, initialSliderPosition, setOffsetX, rootSelector]);

  return (
    <div className="relative size-full" ref={containerRef}>
      <Draggable
        axis="x"
        bounds="parent"
        onDrag={handleDrag}
        position={{x: posX, y: 0}}
      >
        <div className="absolute inset-x-0 z-10 flex h-full w-[2px] items-center justify-center bg-crisp-white">
          <div
            className={classNames('relative h-9 w-7 ', {
              'before:absolute before:inset-0 before:-z-10 before:animate-pulsate before:rounded-full before:bg-crisp-white before:opacity-50 before:shadow-[0_0_0_6px] before:shadow-crisp-white before:blur-[4px]':
                shouldPulsate,
            })}
          >
            <svg
              role="presentation"
              focusable="false"
              width="28"
              height="35"
              viewBox="0 0 32 40"
            >
              <path
                d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16V24C32 32.8366 24.8366 40 16 40C7.16344 40 0 32.8366 0 24V16Z"
                className="fill-crisp-white"
              ></path>
              <path
                d="M11 14H13V26H11zM15 14H17V26H15zM19 14H21V26H19z"
                className="fill-deep-ocean-blue"
              ></path>
            </svg>
          </div>
        </div>
      </Draggable>
    </div>
  );
}
